import {
  SKILL_SLOT_MAX,
  SKILL_SLOT_UNLOCKED_DEFAULT,
  SKILL_COOLDOWN_CARD_CASH_COST,
  SKILL_AP_CARD_CASH_COST,
  SKILL_AP_CARD_SKU,
  SKILL_AOE_RANGE_CARD_CASH_COST,
  SKILL_AOE_RANGE_CARD_SKU,
  SKILL_BUFF_DURATION_CARD_CASH_COST,
  SKILL_BUFF_DURATION_CARD_SKU,
  SKILL_COOLDOWN_CARD_SKU,
  SKILL_CREATE_CARD_CASH_COST,
  SKILL_CREATE_CARD_SKU,
  SKILL_LIBRARY_MAX,
  SKILL_LIBRARY_SLOT_CARD_SKU,
  SKILL_LIBRARY_UNLOCKED_DEFAULT,
  SKILL_SLOT_CARD_SKU,
  skillLibrarySlotUnlockCashCost,
  skillSlotUnlockCashCost,
} from "@/character/skills/constants";
import {
  SHADOW_AP_CARD_CASH_COST,
  SHADOW_AP_CARD_SKU,
  SHADOW_CREATE_CARD_CASH_COST,
  SHADOW_CREATE_CARD_SKU,
} from "@/shadow/constants";
import { HOME_PART_KINDS, kindPreset, type HomePartKind } from "@/home/part-kind";
import type { HomeRoomData } from "@/home/storage";
import { isStashItemId, type StashItemId } from "@/home/home-stash-modal";
import {
  addHealthPotionsToWallet,
  healAmountForTier,
  healthPotionCount,
  HEALTH_POTION_DEFAULT_COIN_PRICE,
  HEALTH_POTION_SKU_BY_TIER,
  type HealthPotionStashItemId,
  type HealthPotionTier,
} from "@/home/health-potions";
import {
  addManaPotionsToWallet,
  manaPotionCount,
  MANA_POTION_DEFAULT_COIN_PRICE,
  MANA_POTION_SKU_BY_TIER,
  restoreAmountForManaTier,
  type ManaPotionStashItemId,
  type ManaPotionTier,
} from "@/home/mana-potions";
import {
  CATALOG_SLOT_PACK_GEM_COST,
  CATALOG_SLOT_PACK_SIZE,
  CATALOG_SLOT_PACK_SKU,
  REVIVE_CARD_CASH_COST,
  REVIVE_CARD_SKU,
  type ShopPurchaseKind,
} from "@/home/shop-definitions";
import {
  normalizeDungeonMonsterGoldBought,
  normalizeDungeonMonsterVault,
} from "@/dungeon/dungeon-monster-vault";

function newPurchaseId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const HOME_ECONOMY_VERSION = 15 as const;

/** Minimum gap between manual hotbar item uses. */
export const ITEM_HOTBAR_USE_COOLDOWN_MS = 1500;

/** Gap between automatic hotbar potion uses. */
export const ITEM_HOTBAR_AUTO_USE_COOLDOWN_MS = 3000;

/** Base catalog slots per kind (upload library). Ready for +bonus from purchases. */
export const CATALOG_SLOT_BASE: Record<HomePartKind, number> = {
  backdrop: 3,
  shell: 3,
  floor: 10,
  solid: 12,
  prop: 16,
  platform: 8,
  attack_dummy: 6,
  monster: 10,
};

export {
  CATALOG_SLOT_PACK_GEM_COST,
  CATALOG_SLOT_PACK_SIZE,
  CATALOG_SLOT_PACK_SKU,
  REVIVE_CARD_CASH_COST,
  REVIVE_CARD_SKU,
} from "@/home/shop-definitions";

export type HomePurchaseKind = ShopPurchaseKind;
export type HomePurchaseStatus = "mock_granted" | "pending" | "paid" | "failed";

export type HomePurchaseRecord = {
  id: string;
  sku: string;
  kind: HomePurchaseKind;
  status: HomePurchaseStatus;
  createdAt: number;
  /** Target catalog kind when buying slots. */
  partKind?: HomePartKind;
  slotsGranted?: number;
  gemSpent?: number;
  cashSpent?: number;
  /** Future payment provider refs. */
  provider?: string;
  providerRef?: string;
};

export type HomeWallet = {
  coins: number;
  gems: number;
  /** Cash Shop currency */
  cash: number;
  healthPotionsSmall: number;
  healthPotionsMedium: number;
  healthPotionsLarge: number;
  manaPotionsSmall: number;
  manaPotionsMedium: number;
  manaPotionsLarge: number;
  /** Consumable — revive after defeat in tower combat. */
  reviveCards: number;
  /** Heals defender home structure HP (not player). */
  homeStructureHpPotions: number;
};

/** @deprecated Use healAmountForTier("small") */
export const HEALTH_POTION_HEAL_AMOUNT = 100;
export const HEALTH_POTION_COIN_COST = HEALTH_POTION_DEFAULT_COIN_PRICE.small;
export const HEALTH_POTION_COIN_SKU = HEALTH_POTION_SKU_BY_TIER.small;
export const STAT_RESET_CARD_SKU = "character.stat_reset_card.v1";

export type HomeEconomy = {
  version: typeof HOME_ECONOMY_VERSION;
  wallet: HomeWallet;
  /**
   * Combat HUD HP after damage/regen. `null` = never hurt (show max until first injury).
   */
  combatHpCurrent: number | null;
  /** Combat HUD mana after spend/regen. `null` = full max until first spend. */
  combatManaCurrent: number | null;
  /** Quick-bar assignments from stash (6 slots: 3 HP + 3 mana by default). */
  itemHotbarSlots: (StashItemId | null)[];
  /** Auto-use potion from slot when combat HP% is below threshold. */
  itemHotbarSlotAuto: boolean[];
  /** Use auto hotbar potions when HP is below this percent (1–99). */
  itemHotbarAutoHpPercent: number;
  /** Use auto hotbar mana potions when mana% is below this percent (1–99). */
  itemHotbarAutoManaPercent: number;
  /** Unlocked skill hotbar slots (3–8). */
  skillSlotsUnlocked: number;
  /** Max skills in library inventory (default 3, up to SKILL_LIBRARY_MAX). */
  skillLibrarySlotsUnlocked: number;
  /** Cards consumed when creating a new skill (not when editing). */
  skillCreateCards: number;
  /** Purchased cooldown reduction cards (each −5s, max 4). */
  skillCooldownCards: number;
  /** Purchased buff AP cards (apply per buff skill at skills UI). */
  skillApCards: number;
  /** Purchased buff duration cards (+0.1s per card applied to a buff skill). */
  skillBuffDurationCards: number;
  /** Purchased AOE range cards (+1% width per card applied to an AOE attack skill). */
  skillAoeRangeCards: number;
  /** Cards consumed when creating a new shadow. */
  shadowCreateCards: number;
  /** Shadow AP cards in stash (apply per shadow). */
  shadowApCards: number;
  petCreateCards: number;
  petApCards: number;
  petSizeCards: number;
  petSkillSlotsUnlocked: number;
  petSkillLibrarySlotsUnlocked: number;
  petSkillCreateCards: number;
  petSkillCooldownCards: number;
  petSkillApCards: number;
  petSkillBuffDurationCards: number;
  petSkillAoeRangeCards: number;
  /** Extra catalog slots from purchases (per kind). */
  slotBonus: Partial<Record<HomePartKind, number>>;
  /** Dungeon defender monsters in warehouse (not assigned to a floor). */
  dungeonMonsterVault: Record<string, number>;
  /** Lifetime gold purchases per monster (dungeon shop cap). */
  dungeonMonsterGoldBought: Record<string, number>;
  /** One-time migration from per-floor monsterAllowance. */
  dungeonMonsterVaultMigrated?: boolean;
  /** Cash-shop cards: +1× monster power per card (dungeon floor editor). */
  dungeonMonsterPowerCards: number;
  homeStructureHpCards: number;
  homeStructureDefCards: number;
  homeStructureRegenCards: number;
  homeStructureHpCardsApplied: number;
  homeStructureDefCardsApplied: number;
  homeStructureRegenCardsApplied: number;
  homeStructureAutoPotionEnabled: boolean;
  homeStructureAutoPotionHpPercent: number;
  purchases: HomePurchaseRecord[];
};

type LegacyEconomy = Partial<HomeEconomy> & {
  wallet?: Partial<HomeWallet>;
};

function normalizeHealthPotionWallet(
  wallet: Partial<HomeWallet> & { healthPotions?: number },
  base: HomeWallet,
): Pick<
  HomeWallet,
  "healthPotionsSmall" | "healthPotionsMedium" | "healthPotionsLarge"
> {
  const hasNewKeys =
    typeof wallet.healthPotionsSmall === "number" ||
    typeof wallet.healthPotionsMedium === "number" ||
    typeof wallet.healthPotionsLarge === "number";
  const legacy = typeof wallet.healthPotions === "number" ? wallet.healthPotions : 0;
  let small = clampNonNeg(wallet.healthPotionsSmall, base.healthPotionsSmall);
  const medium = clampNonNeg(wallet.healthPotionsMedium, base.healthPotionsMedium);
  const large = clampNonNeg(wallet.healthPotionsLarge, base.healthPotionsLarge);
  if (!hasNewKeys && legacy > 0) {
    small += legacy;
  }
  return {
    healthPotionsSmall: small,
    healthPotionsMedium: medium,
    healthPotionsLarge: large,
  };
}

function normalizeManaPotionWallet(
  wallet: Partial<HomeWallet>,
  base: HomeWallet,
): Pick<HomeWallet, "manaPotionsSmall" | "manaPotionsMedium" | "manaPotionsLarge"> {
  return {
    manaPotionsSmall: clampNonNeg(wallet.manaPotionsSmall, base.manaPotionsSmall),
    manaPotionsMedium: clampNonNeg(wallet.manaPotionsMedium, base.manaPotionsMedium),
    manaPotionsLarge: clampNonNeg(wallet.manaPotionsLarge, base.manaPotionsLarge),
  };
}

export const ITEM_HOTBAR_SLOT_COUNT = 6;

export function defaultItemHotbarSlots(): (StashItemId | null)[] {
  return [
    "health_potion_small",
    "health_potion_medium",
    "health_potion_large",
    "mana_potion_small",
    "mana_potion_medium",
    "mana_potion_large",
  ];
}

export function normalizeItemHotbarSlots(raw: unknown): (StashItemId | null)[] {
  const defaults = defaultItemHotbarSlots();
  if (!Array.isArray(raw)) return defaults;
  const out: (StashItemId | null)[] = [];
  for (let i = 0; i < ITEM_HOTBAR_SLOT_COUNT; i += 1) {
    const value = raw[i];
    if (typeof value === "string" && isStashItemId(value)) {
      out.push(value);
    } else {
      out.push(defaults[i] ?? null);
    }
  }
  return out;
}

export function setItemHotbarSlot(
  economy: HomeEconomy,
  index: number,
  itemId: StashItemId | null,
): HomeEconomy {
  if (index < 0 || index >= ITEM_HOTBAR_SLOT_COUNT) return economy;
  const slots = [...economy.itemHotbarSlots];
  slots[index] = itemId;
  return { ...economy, itemHotbarSlots: slots };
}

export function normalizeItemHotbarSlotAuto(raw: unknown): boolean[] {
  const out = Array.from({ length: ITEM_HOTBAR_SLOT_COUNT }, () => false);
  if (!Array.isArray(raw)) return out;
  for (let i = 0; i < ITEM_HOTBAR_SLOT_COUNT; i += 1) {
    out[i] = raw[i] === true;
  }
  return out;
}

export function normalizeItemHotbarAutoHpPercent(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 40;
  return Math.min(99, Math.max(1, Math.round(raw)));
}

export function setItemHotbarSlotAuto(
  economy: HomeEconomy,
  index: number,
  enabled: boolean,
): HomeEconomy {
  if (index < 0 || index >= ITEM_HOTBAR_SLOT_COUNT) return economy;
  const next = [...economy.itemHotbarSlotAuto];
  next[index] = enabled;
  return { ...economy, itemHotbarSlotAuto: next };
}

export function setItemHotbarAutoHpPercent(
  economy: HomeEconomy,
  percent: number,
): HomeEconomy {
  const next = normalizeItemHotbarAutoHpPercent(percent);
  if (economy.itemHotbarAutoHpPercent === next) return economy;
  return { ...economy, itemHotbarAutoHpPercent: next };
}

export function normalizeItemHotbarAutoManaPercent(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 40;
  return Math.min(99, Math.max(1, Math.round(raw)));
}

export function setItemHotbarAutoManaPercent(
  economy: HomeEconomy,
  percent: number,
): HomeEconomy {
  const next = normalizeItemHotbarAutoManaPercent(percent);
  if (economy.itemHotbarAutoManaPercent === next) return economy;
  return { ...economy, itemHotbarAutoManaPercent: next };
}

export function createDefaultHomeEconomy(): HomeEconomy {
  return {
    version: HOME_ECONOMY_VERSION,
    wallet: {
      coins: 12_580,
      gems: 320,
      cash: 500,
      healthPotionsSmall: 2,
      healthPotionsMedium: 1,
      healthPotionsLarge: 0,
      manaPotionsSmall: 2,
      manaPotionsMedium: 1,
      manaPotionsLarge: 0,
      reviveCards: 0,
      homeStructureHpPotions: 0,
    },
    combatHpCurrent: null,
    combatManaCurrent: null,
    itemHotbarSlots: normalizeItemHotbarSlots(undefined),
    itemHotbarSlotAuto: normalizeItemHotbarSlotAuto(undefined),
    itemHotbarAutoHpPercent: normalizeItemHotbarAutoHpPercent(undefined),
    itemHotbarAutoManaPercent: normalizeItemHotbarAutoManaPercent(undefined),
    skillSlotsUnlocked: SKILL_SLOT_UNLOCKED_DEFAULT,
    skillLibrarySlotsUnlocked: SKILL_LIBRARY_UNLOCKED_DEFAULT,
    skillCreateCards: SKILL_LIBRARY_UNLOCKED_DEFAULT,
    skillCooldownCards: 0,
    skillApCards: 0,
    skillBuffDurationCards: 0,
    skillAoeRangeCards: 0,
    shadowCreateCards: 0,
    shadowApCards: 0,
    petCreateCards: 0,
    petApCards: 0,
    petSizeCards: 0,
    petSkillSlotsUnlocked: SKILL_SLOT_UNLOCKED_DEFAULT,
    petSkillLibrarySlotsUnlocked: SKILL_LIBRARY_UNLOCKED_DEFAULT,
    petSkillCreateCards: SKILL_LIBRARY_UNLOCKED_DEFAULT,
    petSkillCooldownCards: 0,
    petSkillApCards: 0,
    petSkillBuffDurationCards: 0,
    petSkillAoeRangeCards: 0,
    slotBonus: {},
    dungeonMonsterVault: {},
    dungeonMonsterGoldBought: {},
    dungeonMonsterVaultMigrated: false,
    dungeonMonsterPowerCards: 0,
    homeStructureHpCards: 0,
    homeStructureDefCards: 0,
    homeStructureRegenCards: 0,
    homeStructureHpCardsApplied: 0,
    homeStructureDefCardsApplied: 0,
    homeStructureRegenCardsApplied: 0,
    homeStructureAutoPotionEnabled: true,
    homeStructureAutoPotionHpPercent: 50,
    purchases: [],
  };
}

export function normalizeCombatHpCurrent(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.floor(value));
}

export function setCombatHpCurrent(
  economy: HomeEconomy,
  current: number,
): HomeEconomy {
  const next = Math.max(0, Math.floor(current));
  if (economy.combatHpCurrent === next) return economy;
  return { ...economy, combatHpCurrent: next };
}

/** Persist when injured or already tracking HP (incl. healed back to full). */
export function shouldPersistCombatHp(
  economy: HomeEconomy,
  current: number,
  maxHp: number,
): boolean {
  const clamped = Math.max(0, Math.min(maxHp, Math.floor(current)));
  if (economy.combatHpCurrent !== null) return true;
  return clamped < maxHp;
}

export function setCombatManaCurrent(
  economy: HomeEconomy,
  current: number,
): HomeEconomy {
  const next = Math.max(0, Math.floor(current));
  if (economy.combatManaCurrent === next) return economy;
  return { ...economy, combatManaCurrent: next };
}

export function shouldPersistCombatMana(
  economy: HomeEconomy,
  current: number,
  maxMana: number,
): boolean {
  const clamped = Math.max(0, Math.min(maxMana, Math.floor(current)));
  if (economy.combatManaCurrent !== null) return true;
  return clamped < maxMana;
}

export function normalizeHomeEconomy(raw: unknown): HomeEconomy {
  const base = createDefaultHomeEconomy();
  if (!raw || typeof raw !== "object") return base;
  const parsed = raw as LegacyEconomy;
  const wallet: Partial<HomeWallet> = parsed.wallet ?? {};
  const slotBonus: Partial<Record<HomePartKind, number>> = {};
  if (parsed.slotBonus && typeof parsed.slotBonus === "object") {
    for (const kind of HOME_PART_KINDS) {
      const n = (parsed.slotBonus as Record<string, unknown>)[kind];
      if (typeof n === "number" && Number.isFinite(n) && n > 0) {
        slotBonus[kind] = Math.floor(n);
      }
    }
  }
  const purchases = Array.isArray(parsed.purchases)
    ? parsed.purchases.filter(isPurchaseRecord)
    : [];

  const skillSlotsUnlocked = clampSkillSlots(
    (parsed as { skillSlotsUnlocked?: number }).skillSlotsUnlocked,
    base.skillSlotsUnlocked,
  );
  const skillLibrarySlotsUnlocked = clampSkillLibrarySlots(
    (parsed as { skillLibrarySlotsUnlocked?: number }).skillLibrarySlotsUnlocked,
    base.skillLibrarySlotsUnlocked,
  );
  const parsedCreateCards = (parsed as { skillCreateCards?: number }).skillCreateCards;
  const skillCreateCards =
    typeof parsedCreateCards === "number"
      ? clampSkillCardInventory(parsedCreateCards, base.skillCreateCards)
      : typeof parsed.version === "number" && parsed.version < HOME_ECONOMY_VERSION
        ? 0
        : base.skillCreateCards;
  const skillCooldownCards = clampSkillCardInventory(
    (parsed as { skillCooldownCards?: number }).skillCooldownCards,
    base.skillCooldownCards,
  );
  const skillApCards = clampSkillApInventory(
    (parsed as { skillApCards?: number }).skillApCards,
    base.skillApCards,
  );
  const skillBuffDurationCards = clampSkillApInventory(
    (parsed as { skillBuffDurationCards?: number }).skillBuffDurationCards,
    base.skillBuffDurationCards,
  );
  const skillAoeRangeCards = clampSkillApInventory(
    (parsed as { skillAoeRangeCards?: number }).skillAoeRangeCards,
    base.skillAoeRangeCards,
  );
  const shadowCreateCards = clampSkillApInventory(
    (parsed as { shadowCreateCards?: number }).shadowCreateCards,
    base.shadowCreateCards,
  );
  const shadowApCards = clampSkillApInventory(
    (parsed as { shadowApCards?: number }).shadowApCards,
    base.shadowApCards,
  );
  const petCreateCards = clampSkillApInventory(
    (parsed as { petCreateCards?: number }).petCreateCards,
    base.petCreateCards,
  );
  const petApCards = clampSkillApInventory(
    (parsed as { petApCards?: number }).petApCards,
    base.petApCards,
  );
  const petSizeCards = clampSkillApInventory(
    (parsed as { petSizeCards?: number }).petSizeCards,
    base.petSizeCards,
  );
  const petSkillSlotsUnlocked = clampSkillSlots(
    (parsed as { petSkillSlotsUnlocked?: number }).petSkillSlotsUnlocked,
    base.petSkillSlotsUnlocked,
  );
  const petSkillLibrarySlotsUnlocked = clampSkillLibrarySlots(
    (parsed as { petSkillLibrarySlotsUnlocked?: number }).petSkillLibrarySlotsUnlocked,
    base.petSkillLibrarySlotsUnlocked,
  );
  const petSkillCreateCards = clampSkillCardInventory(
    (parsed as { petSkillCreateCards?: number }).petSkillCreateCards,
    base.petSkillCreateCards,
  );
  const petSkillCooldownCards = clampSkillCardInventory(
    (parsed as { petSkillCooldownCards?: number }).petSkillCooldownCards,
    base.petSkillCooldownCards,
  );
  const petSkillApCards = clampSkillApInventory(
    (parsed as { petSkillApCards?: number }).petSkillApCards,
    base.petSkillApCards,
  );
  const petSkillBuffDurationCards = clampSkillApInventory(
    (parsed as { petSkillBuffDurationCards?: number }).petSkillBuffDurationCards,
    base.petSkillBuffDurationCards,
  );
  const petSkillAoeRangeCards = clampSkillApInventory(
    (parsed as { petSkillAoeRangeCards?: number }).petSkillAoeRangeCards,
    base.petSkillAoeRangeCards,
  );

  return {
    version: HOME_ECONOMY_VERSION,
    combatHpCurrent: normalizeCombatHpCurrent(
      (parsed as { combatHpCurrent?: unknown }).combatHpCurrent,
    ),
    combatManaCurrent: normalizeCombatHpCurrent(
      (parsed as { combatManaCurrent?: unknown }).combatManaCurrent,
    ),
    itemHotbarSlots: normalizeItemHotbarSlots(
      (parsed as { itemHotbarSlots?: unknown }).itemHotbarSlots,
    ),
    itemHotbarSlotAuto: normalizeItemHotbarSlotAuto(
      (parsed as { itemHotbarSlotAuto?: unknown }).itemHotbarSlotAuto,
    ),
    itemHotbarAutoHpPercent: normalizeItemHotbarAutoHpPercent(
      (parsed as { itemHotbarAutoHpPercent?: unknown }).itemHotbarAutoHpPercent,
    ),
    itemHotbarAutoManaPercent: normalizeItemHotbarAutoManaPercent(
      (parsed as { itemHotbarAutoManaPercent?: unknown }).itemHotbarAutoManaPercent,
    ),
    wallet: {
      coins: clampNonNeg(wallet.coins, base.wallet.coins),
      gems: clampNonNeg(wallet.gems, base.wallet.gems),
      cash: clampNonNeg(wallet.cash, base.wallet.cash),
      ...normalizeHealthPotionWallet(wallet, base.wallet),
      ...normalizeManaPotionWallet(wallet, base.wallet),
      reviveCards: clampNonNeg(wallet.reviveCards, base.wallet.reviveCards),
      homeStructureHpPotions: clampNonNeg(
        wallet.homeStructureHpPotions,
        base.wallet.homeStructureHpPotions,
      ),
    },
    skillSlotsUnlocked,
    skillLibrarySlotsUnlocked,
    skillCreateCards,
    skillCooldownCards,
    skillApCards,
    skillBuffDurationCards,
    skillAoeRangeCards,
    shadowCreateCards,
    shadowApCards,
    petCreateCards,
    petApCards,
    petSizeCards,
    petSkillSlotsUnlocked,
    petSkillLibrarySlotsUnlocked,
    petSkillCreateCards,
    petSkillCooldownCards,
    petSkillApCards,
    petSkillBuffDurationCards,
    petSkillAoeRangeCards,
    slotBonus,
    dungeonMonsterVault: normalizeDungeonMonsterVault(
      (parsed as { dungeonMonsterVault?: unknown }).dungeonMonsterVault,
    ),
    dungeonMonsterGoldBought: normalizeDungeonMonsterGoldBought(
      (parsed as { dungeonMonsterGoldBought?: unknown }).dungeonMonsterGoldBought,
    ),
    dungeonMonsterVaultMigrated:
      (parsed as { dungeonMonsterVaultMigrated?: unknown }).dungeonMonsterVaultMigrated === true,
    dungeonMonsterPowerCards: clampNonNeg(
      (parsed as { dungeonMonsterPowerCards?: unknown }).dungeonMonsterPowerCards,
      base.dungeonMonsterPowerCards,
    ),
    homeStructureHpCards: clampNonNeg(
      (parsed as { homeStructureHpCards?: unknown }).homeStructureHpCards,
      base.homeStructureHpCards,
    ),
    homeStructureDefCards: clampNonNeg(
      (parsed as { homeStructureDefCards?: unknown }).homeStructureDefCards,
      base.homeStructureDefCards,
    ),
    homeStructureRegenCards: clampNonNeg(
      (parsed as { homeStructureRegenCards?: unknown }).homeStructureRegenCards,
      base.homeStructureRegenCards,
    ),
    homeStructureHpCardsApplied: clampNonNeg(
      (parsed as { homeStructureHpCardsApplied?: unknown }).homeStructureHpCardsApplied,
      base.homeStructureHpCardsApplied,
    ),
    homeStructureDefCardsApplied: clampNonNeg(
      (parsed as { homeStructureDefCardsApplied?: unknown }).homeStructureDefCardsApplied,
      base.homeStructureDefCardsApplied,
    ),
    homeStructureRegenCardsApplied: clampNonNeg(
      (parsed as { homeStructureRegenCardsApplied?: unknown }).homeStructureRegenCardsApplied,
      base.homeStructureRegenCardsApplied,
    ),
    homeStructureAutoPotionEnabled:
      (parsed as { homeStructureAutoPotionEnabled?: unknown }).homeStructureAutoPotionEnabled !==
      false,
    homeStructureAutoPotionHpPercent: (() => {
      const v = (parsed as { homeStructureAutoPotionHpPercent?: unknown })
        .homeStructureAutoPotionHpPercent;
      if (typeof v === "number" && Number.isFinite(v)) {
        return Math.min(99, Math.max(1, Math.floor(v)));
      }
      return base.homeStructureAutoPotionHpPercent;
    })(),
    purchases,
  };
}

function clampSkillSlots(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(SKILL_SLOT_MAX, Math.max(SKILL_SLOT_UNLOCKED_DEFAULT, Math.floor(value)));
}

function clampSkillLibrarySlots(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(
    SKILL_LIBRARY_MAX,
    Math.max(SKILL_LIBRARY_UNLOCKED_DEFAULT, Math.floor(value)),
  );
}

function clampSkillCardInventory(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

function clampSkillApInventory(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

function clampNonNeg(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(value));
}

function isPurchaseRecord(value: unknown): value is HomePurchaseRecord {
  if (!value || typeof value !== "object") return false;
  const p = value as HomePurchaseRecord;
  return (
    typeof p.id === "string" &&
    typeof p.sku === "string" &&
    typeof p.kind === "string" &&
    typeof p.status === "string" &&
    typeof p.createdAt === "number"
  );
}

export type WalletGrant = {
  coins?: number;
  gems?: number;
  cash?: number;
};

/** Add non-negative integer amounts to wallet currencies. */
export function applyWalletGrant(economy: HomeEconomy, grant: WalletGrant): HomeEconomy {
  const add = (n: number | undefined) =>
    typeof n === "number" && Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  const coins = add(grant.coins);
  const gems = add(grant.gems);
  const cash = add(grant.cash);
  if (coins === 0 && gems === 0 && cash === 0) return economy;
  return {
    ...economy,
    wallet: {
      ...economy.wallet,
      coins: economy.wallet.coins + coins,
      gems: economy.wallet.gems + gems,
      cash: economy.wallet.cash + cash,
    },
  };
}

export function catalogSlotBonus(economy: HomeEconomy, kind: HomePartKind): number {
  return economy.slotBonus[kind] ?? 0;
}

export function catalogSlotCap(economy: HomeEconomy, kind: HomePartKind): number {
  return CATALOG_SLOT_BASE[kind] + catalogSlotBonus(economy, kind);
}

export function catalogSlotUsed(data: HomeRoomData, kind: HomePartKind): number {
  return data.parts.reduce(
    (n, part) => (part.kind === kind ? n + 1 : n),
    0,
  );
}

export function catalogSlotRemaining(
  data: HomeRoomData,
  economy: HomeEconomy,
  kind: HomePartKind,
): number {
  return Math.max(0, catalogSlotCap(economy, kind) - catalogSlotUsed(data, kind));
}

export function canAddCatalogPart(
  data: HomeRoomData,
  economy: HomeEconomy,
  kind: HomePartKind,
): boolean {
  return catalogSlotRemaining(data, economy, kind) > 0;
}

export function totalCatalogCap(economy: HomeEconomy): number {
  return HOME_PART_KINDS.reduce((n, kind) => n + catalogSlotCap(economy, kind), 0);
}

export function totalCatalogUsed(data: HomeRoomData): number {
  return data.parts.length;
}

export type BuySlotResult =
  | { ok: true; economy: HomeEconomy; granted: number; kind: HomePartKind }
  | { ok: false; reason: string };

/** Mock grant — same shape future paid webhook will produce. */
export function buyCatalogSlotPackMock(
  economy: HomeEconomy,
  kind: HomePartKind,
): BuySlotResult {
  if (economy.wallet.gems < CATALOG_SLOT_PACK_GEM_COST) {
    return {
      ok: false,
      reason: `เพชรไม่พอ · ต้องการ ${CATALOG_SLOT_PACK_GEM_COST}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: CATALOG_SLOT_PACK_SKU,
    kind: "slot_pack",
    status: "mock_granted",
    createdAt: Date.now(),
    partKind: kind,
    slotsGranted: CATALOG_SLOT_PACK_SIZE,
    gemSpent: CATALOG_SLOT_PACK_GEM_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      gems: economy.wallet.gems - CATALOG_SLOT_PACK_GEM_COST,
    },
    slotBonus: {
      ...economy.slotBonus,
      [kind]: catalogSlotBonus(economy, kind) + CATALOG_SLOT_PACK_SIZE,
    },
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, granted: CATALOG_SLOT_PACK_SIZE, kind };
}

export type BuySkillSlotResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillSlotCardMock(economy: HomeEconomy): BuySkillSlotResult {
  const unlocked = economy.skillSlotsUnlocked;
  const cost = skillSlotUnlockCashCost(unlocked);
  if (cost === null) {
    return { ok: false, reason: "ช่องสกิลเต็มแล้ว (8/8)" };
  }
  if (economy.wallet.cash < cost) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${cost.toLocaleString("en-US")}` };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_SLOT_CARD_SKU,
    kind: "skill_slot_card",
    status: "mock_granted",
    createdAt: Date.now(),
    slotsGranted: 1,
    cashSpent: cost,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - cost,
    },
    skillSlotsUnlocked: Math.min(SKILL_SLOT_MAX, unlocked + 1),
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: cost };
}

export type BuySkillLibrarySlotResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillLibrarySlotCardMock(economy: HomeEconomy): BuySkillLibrarySlotResult {
  const unlocked = economy.skillLibrarySlotsUnlocked;
  const cost = skillLibrarySlotUnlockCashCost(unlocked);
  if (cost === null) {
    return { ok: false, reason: `คลังสกิลเต็มแล้ว (${SKILL_LIBRARY_MAX}/${SKILL_LIBRARY_MAX})` };
  }
  if (economy.wallet.cash < cost) {
    return { ok: false, reason: `Cash ไม่พอ · ต้องการ ${cost.toLocaleString("en-US")}` };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_LIBRARY_SLOT_CARD_SKU,
    kind: "skill_library_slot_card",
    status: "mock_granted",
    createdAt: Date.now(),
    slotsGranted: 1,
    cashSpent: cost,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - cost,
    },
    skillLibrarySlotsUnlocked: Math.min(SKILL_LIBRARY_MAX, unlocked + 1),
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: cost };
}

export type BuySkillCreateCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillCreateCardMock(economy: HomeEconomy): BuySkillCreateCardResult {
  if (economy.wallet.cash < SKILL_CREATE_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SKILL_CREATE_CARD_CASH_COST}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_CREATE_CARD_SKU,
    kind: "skill_create_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SKILL_CREATE_CARD_CASH_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SKILL_CREATE_CARD_CASH_COST,
    },
    skillCreateCards: economy.skillCreateCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: SKILL_CREATE_CARD_CASH_COST };
}

export type BuyShadowCreateCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buyShadowCreateCardMock(economy: HomeEconomy): BuyShadowCreateCardResult {
  if (economy.wallet.cash < SHADOW_CREATE_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SHADOW_CREATE_CARD_CASH_COST}`,
    };
  }
  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SHADOW_CREATE_CARD_SKU,
    kind: "shadow_create_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SHADOW_CREATE_CARD_CASH_COST,
    provider: "mock",
  };
  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SHADOW_CREATE_CARD_CASH_COST,
    },
    shadowCreateCards: economy.shadowCreateCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };
  return { ok: true, economy: next, cashSpent: SHADOW_CREATE_CARD_CASH_COST };
}

export type BuyShadowApCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buyShadowApCardMock(economy: HomeEconomy): BuyShadowApCardResult {
  if (economy.wallet.cash < SHADOW_AP_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SHADOW_AP_CARD_CASH_COST}`,
    };
  }
  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SHADOW_AP_CARD_SKU,
    kind: "shadow_ap_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SHADOW_AP_CARD_CASH_COST,
    provider: "mock",
  };
  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SHADOW_AP_CARD_CASH_COST,
    },
    shadowApCards: economy.shadowApCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };
  return { ok: true, economy: next, cashSpent: SHADOW_AP_CARD_CASH_COST };
}

export function consumeShadowCreateCard(economy: HomeEconomy): HomeEconomy | null {
  if (economy.shadowCreateCards < 1) return null;
  return { ...economy, shadowCreateCards: economy.shadowCreateCards - 1 };
}

export function consumePetCreateCard(economy: HomeEconomy): HomeEconomy | null {
  if (economy.petCreateCards < 1) return null;
  return { ...economy, petCreateCards: economy.petCreateCards - 1 };
}

export type BuyCooldownCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillCooldownCardMock(economy: HomeEconomy): BuyCooldownCardResult {
  if (economy.wallet.cash < SKILL_COOLDOWN_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SKILL_COOLDOWN_CARD_CASH_COST}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_COOLDOWN_CARD_SKU,
    kind: "skill_cooldown_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SKILL_COOLDOWN_CARD_CASH_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SKILL_COOLDOWN_CARD_CASH_COST,
    },
    skillCooldownCards: economy.skillCooldownCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: SKILL_COOLDOWN_CARD_CASH_COST };
}

export type BuySkillApCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillApCardMock(economy: HomeEconomy): BuySkillApCardResult {
  if (economy.wallet.cash < SKILL_AP_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SKILL_AP_CARD_CASH_COST}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_AP_CARD_SKU,
    kind: "skill_ap_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SKILL_AP_CARD_CASH_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SKILL_AP_CARD_CASH_COST,
    },
    skillApCards: economy.skillApCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: SKILL_AP_CARD_CASH_COST };
}

export type BuySkillBuffDurationCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillBuffDurationCardMock(
  economy: HomeEconomy,
): BuySkillBuffDurationCardResult {
  if (economy.wallet.cash < SKILL_BUFF_DURATION_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SKILL_BUFF_DURATION_CARD_CASH_COST}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_BUFF_DURATION_CARD_SKU,
    kind: "skill_buff_duration_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SKILL_BUFF_DURATION_CARD_CASH_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SKILL_BUFF_DURATION_CARD_CASH_COST,
    },
    skillBuffDurationCards: economy.skillBuffDurationCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: SKILL_BUFF_DURATION_CARD_CASH_COST };
}

export type BuySkillAoeRangeCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buySkillAoeRangeCardMock(
  economy: HomeEconomy,
): BuySkillAoeRangeCardResult {
  if (economy.wallet.cash < SKILL_AOE_RANGE_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${SKILL_AOE_RANGE_CARD_CASH_COST}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: SKILL_AOE_RANGE_CARD_SKU,
    kind: "skill_aoe_range_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: SKILL_AOE_RANGE_CARD_CASH_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - SKILL_AOE_RANGE_CARD_CASH_COST,
    },
    skillAoeRangeCards: economy.skillAoeRangeCards + 1,
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: SKILL_AOE_RANGE_CARD_CASH_COST };
}

export type BuyReviveCardResult =
  | { ok: true; economy: HomeEconomy; cashSpent: number }
  | { ok: false; reason: string };

export function buyReviveCardMock(economy: HomeEconomy): BuyReviveCardResult {
  if (economy.wallet.cash < REVIVE_CARD_CASH_COST) {
    return {
      ok: false,
      reason: `Cash ไม่พอ · ต้องการ ${REVIVE_CARD_CASH_COST.toLocaleString("en-US")}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: REVIVE_CARD_SKU,
    kind: "revive_card",
    status: "mock_granted",
    createdAt: Date.now(),
    cashSpent: REVIVE_CARD_CASH_COST,
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: {
      ...economy.wallet,
      cash: economy.wallet.cash - REVIVE_CARD_CASH_COST,
      reviveCards: economy.wallet.reviveCards + 1,
    },
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, cashSpent: REVIVE_CARD_CASH_COST };
}

export type UseReviveCardResult =
  | { ok: true; economy: HomeEconomy }
  | { ok: false; reason: string };

export function useReviveCard(economy: HomeEconomy): UseReviveCardResult {
  if (economy.wallet.reviveCards <= 0) {
    return { ok: false, reason: "ไม่มีบัตรชุบชีวิต" };
  }
  return {
    ok: true,
    economy: {
      ...economy,
      wallet: {
        ...economy.wallet,
        reviveCards: economy.wallet.reviveCards - 1,
      },
    },
  };
}

export function slotLabelTh(kind: HomePartKind): string {
  return kindPreset(kind).tabLabelTh;
}

/** Filter drafts that fit remaining slots (preserves order). */
export function takeDraftsWithinSlots<T extends { kind?: string }>(
  data: HomeRoomData,
  economy: HomeEconomy,
  drafts: T[],
): { accepted: T[]; skipped: number } {
  const used: Record<HomePartKind, number> = {
    backdrop: catalogSlotUsed(data, "backdrop"),
    shell: catalogSlotUsed(data, "shell"),
    floor: catalogSlotUsed(data, "floor"),
    solid: catalogSlotUsed(data, "solid"),
    prop: catalogSlotUsed(data, "prop"),
    platform: catalogSlotUsed(data, "platform"),
    attack_dummy: catalogSlotUsed(data, "attack_dummy"),
    monster: catalogSlotUsed(data, "monster"),
  };
  const accepted: T[] = [];
  let skipped = 0;
  for (const draft of drafts) {
    const kind = (HOME_PART_KINDS as readonly string[]).includes(String(draft.kind))
      ? (draft.kind as HomePartKind)
      : "solid";
    const cap = catalogSlotCap(economy, kind);
    if (used[kind] >= cap) {
      skipped += 1;
      continue;
    }
    used[kind] += 1;
    accepted.push(draft);
  }
  return { accepted, skipped };
}

export type UseHealthPotionResult =
  | { ok: true; economy: HomeEconomy; healed: number }
  | { ok: false; reason: string };

export type BuyHealthPotionResult =
  | { ok: true; economy: HomeEconomy; coinsSpent: number }
  | { ok: false; reason: string };

export function buyHealthPotionWithCoinsMock(
  economy: HomeEconomy,
  tier: HealthPotionTier = "small",
): BuyHealthPotionResult {
  const cost = HEALTH_POTION_DEFAULT_COIN_PRICE[tier];
  if (economy.wallet.coins < cost) {
    return {
      ok: false,
      reason: `ทองไม่พอ · ต้องการ ${cost.toLocaleString("en-US")}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: HEALTH_POTION_SKU_BY_TIER[tier],
    kind: "health_potion",
    status: "mock_granted",
    createdAt: Date.now(),
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: addHealthPotionsToWallet(
      {
        ...economy.wallet,
        coins: economy.wallet.coins - cost,
      },
      tier,
      1,
    ),
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, coinsSpent: cost };
}

export type StashDiscardItemId =
  | HealthPotionStashItemId
  | ManaPotionStashItemId
  | "revive_card"
  | "skill_cooldown_card"
  | "skill_ap_card"
  | "skill_buff_duration_card"
  | "skill_aoe_range_card"
  | "skill_create_card"
  | "shadow_create_card"
  | "shadow_ap_card";

export type DiscardStashResult =
  | { ok: true; economy: HomeEconomy; discarded: number }
  | { ok: false; reason: string };

export function discardStashItems(
  economy: HomeEconomy,
  itemId: StashDiscardItemId,
  quantity: number,
): DiscardStashResult {
  const qty = Math.max(1, Math.floor(quantity));
  switch (itemId) {
    case "health_potion_small":
    case "health_potion_medium":
    case "health_potion_large": {
      const tier =
        itemId === "health_potion_small"
          ? "small"
          : itemId === "health_potion_medium"
            ? "medium"
            : "large";
      if (healthPotionCount(economy, tier) < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          wallet: addHealthPotionsToWallet(economy.wallet, tier, -qty),
        },
      };
    }
    case "mana_potion_small":
    case "mana_potion_medium":
    case "mana_potion_large": {
      const tier =
        itemId === "mana_potion_small"
          ? "small"
          : itemId === "mana_potion_medium"
            ? "medium"
            : "large";
      if (manaPotionCount(economy, tier) < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          wallet: addManaPotionsToWallet(economy.wallet, tier, -qty),
        },
      };
    }
    case "revive_card": {
      if (economy.wallet.reviveCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          wallet: {
            ...economy.wallet,
            reviveCards: economy.wallet.reviveCards - qty,
          },
        },
      };
    }
    case "skill_cooldown_card": {
      if (economy.skillCooldownCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          skillCooldownCards: economy.skillCooldownCards - qty,
        },
      };
    }
    case "skill_ap_card": {
      if (economy.skillApCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          skillApCards: economy.skillApCards - qty,
        },
      };
    }
    case "skill_buff_duration_card": {
      if (economy.skillBuffDurationCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          skillBuffDurationCards: economy.skillBuffDurationCards - qty,
        },
      };
    }
    case "skill_aoe_range_card": {
      if (economy.skillAoeRangeCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          skillAoeRangeCards: economy.skillAoeRangeCards - qty,
        },
      };
    }
    case "skill_create_card": {
      if (economy.skillCreateCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          skillCreateCards: economy.skillCreateCards - qty,
        },
      };
    }
    case "shadow_create_card": {
      if (economy.shadowCreateCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          shadowCreateCards: economy.shadowCreateCards - qty,
        },
      };
    }
    case "shadow_ap_card": {
      if (economy.shadowApCards < qty) {
        return { ok: false, reason: "จำนวนในคลังไม่พอ" };
      }
      return {
        ok: true,
        discarded: qty,
        economy: {
          ...economy,
          shadowApCards: economy.shadowApCards - qty,
        },
      };
    }
    default:
      return { ok: false, reason: "ทิ้งไอเทมนี้ไม่ได้" };
  }
}

export type UseHealthPotionCombatHp = {
  current: number;
  max: number;
};

export function useHealthPotion(
  economy: HomeEconomy,
  tier: HealthPotionTier,
  combatHp?: UseHealthPotionCombatHp,
): UseHealthPotionResult {
  if (healthPotionCount(economy, tier) <= 0) {
    return { ok: false, reason: "ไม่มียาขนาดนี้" };
  }
  const healCap = healAmountForTier(tier);
  if (!combatHp) {
    return { ok: false, reason: "ใช้ยาเลือดได้เมื่อมีแถบ HP" };
  }
  if (combatHp.current >= combatHp.max) {
    return { ok: false, reason: "เลือดเต็มแล้ว" };
  }
  const before = combatHp.current;
  const after = Math.min(combatHp.max, before + healCap);
  return {
    ok: true,
    economy: {
      ...economy,
      wallet: addHealthPotionsToWallet(economy.wallet, tier, -1),
    },
    healed: after - before,
  };
}

export type UseManaPotionCombatMana = {
  current: number;
  max: number;
};

export type UseManaPotionResult =
  | { ok: true; economy: HomeEconomy; restored: number }
  | { ok: false; reason: string };

export function useManaPotion(
  economy: HomeEconomy,
  tier: ManaPotionTier,
  combatMana: UseManaPotionCombatMana,
): UseManaPotionResult {
  if (manaPotionCount(economy, tier) <= 0) {
    return { ok: false, reason: "ไม่มียามานาขนาดนี้" };
  }
  const restoreCap = restoreAmountForManaTier(tier);
  if (combatMana.current >= combatMana.max) {
    return { ok: false, reason: "มานาเต็มแล้ว" };
  }
  const before = combatMana.current;
  const after = Math.min(combatMana.max, before + restoreCap);
  return {
    ok: true,
    economy: {
      ...economy,
      wallet: addManaPotionsToWallet(economy.wallet, tier, -1),
    },
    restored: after - before,
  };
}

export type BuyManaPotionResult =
  | { ok: true; economy: HomeEconomy; coinsSpent: number }
  | { ok: false; reason: string };

export function buyManaPotionWithCoinsMock(
  economy: HomeEconomy,
  tier: ManaPotionTier = "small",
): BuyManaPotionResult {
  const cost = MANA_POTION_DEFAULT_COIN_PRICE[tier];
  if (economy.wallet.coins < cost) {
    return {
      ok: false,
      reason: `ทองไม่พอ · ต้องการ ${cost.toLocaleString("en-US")}`,
    };
  }

  const record: HomePurchaseRecord = {
    id: newPurchaseId(),
    sku: MANA_POTION_SKU_BY_TIER[tier],
    kind: "mana_potion",
    status: "mock_granted",
    createdAt: Date.now(),
    provider: "mock",
  };

  const next: HomeEconomy = {
    ...economy,
    wallet: addManaPotionsToWallet(
      {
        ...economy.wallet,
        coins: economy.wallet.coins - cost,
      },
      tier,
      1,
    ),
    purchases: [record, ...economy.purchases].slice(0, 50),
  };

  return { ok: true, economy: next, coinsSpent: cost };
}

/** Grant bonus EXP (quests, item cards). */
export function grantBonusExpAmount(amount: number): number {
  return Math.max(0, Math.floor(amount));
}
